import { ArchivioPezzi, ordinaPezzi, PezzoInAttesa } from './verbaliArchivioPezzi';
import { aggiungiSegnaposto, erroreRichiesta, inviaPezzo } from './verbaliAudioService';

// Coda di invio dei pezzi della registrazione (e dei momenti segnati): ogni pezzo va prima
// nell'archivio locale e se ne toglie SOLO dopo una risposta 2xx del server. Rete assente, 408,
// 429 e 5xx ⇒ nuovi tentativi con attese crescenti (2, 5, 15, 30, 60 s, poi sempre 60 s);
// gli altri 4xx e le eccezioni che non sono di rete ⇒ la coda si ferma e avvisa (il pezzo resta
// nell'archivio). Il ritorno della rete (`online`) o `svuota()` la fanno ripartire. Un pezzo che
// l'archivio non riesce a salvare resta solo in memoria: `onAvviso` lo dice (una volta) e
// `soloInMemoria()` resta vero finché non è arrivato al server.

export const ATTESE_TENTATIVI_MS = [2000, 5000, 15000, 30000, 60000];

export const AVVISO_SOLO_MEMORIA = 'I pezzi non si salvano su questo computer: non chiudere la pagina finché non sono stati inviati';
export const AVVISO_ARCHIVIO_NON_PULITO = 'L\'archivio del browser non risponde: alcuni pezzi già inviati potrebbero essere inviati di nuovo';
const MESSAGGIO_IMPREVISTO = 'Errore imprevisto nell\'invio della registrazione';

/** Rifiuti dei segnaposti che non si risolvono riprovando: il segnaposto si scarta. */
const STATI_SCARTO_SEGNAPOSTO = [400, 404, 409];

/** Errore da non riprovare (es. pezzo senza byte): ferma la coda come un 4xx. */
export class ErroreDefinitivo extends Error {
  constructor(messaggio: string) {
    super(messaggio);
    this.name = 'ErroreDefinitivo';
  }
}

const definitivo = (err: any) => err instanceof ErroreDefinitivo || err?.name === 'ErroreDefinitivo';

/** Errore di rete = errore di axios senza risposta; qualunque altra eccezione è definitiva. */
const diRete = (err: any) => !!err?.isAxiosError && !err.response;
const statoHttp = (err: any): number | null => (typeof err?.response?.status === 'number' ? err.response.status : null);

function riprovabile(err: any): boolean {
  if (definitivo(err)) return false;
  if (diRete(err)) return true;
  const status = statoHttp(err);
  return status !== null && (status === 408 || status === 429 || status >= 500);
}

function messaggioDi(err: any): string {
  if (definitivo(err)) return String(err.message);
  if (diRete(err) || statoHttp(err) !== null) return erroreRichiesta(err).messaggio;
  return (typeof err?.message === 'string' && err.message) || MESSAGGIO_IMPREVISTO;
}

/** Segnaposto rifiutato in modo definitivo (dati non validi, 400/404/409): si scarta. */
const segnapostoDaScartare = (err: any) => definitivo(err) || STATI_SCARTO_SEGNAPOSTO.includes(statoHttp(err) as number);

const attesaPredefinita = (ms: number) => new Promise<void>(resolve => { setTimeout(resolve, ms); });

export interface OpzioniCodaInvio {
  archivio: ArchivioPezzi;
  invia: (p: PezzoInAttesa) => Promise<void>;
  attendi?: (ms: number) => Promise<void>;
  /** Evento di cui `svuota()` rilegge l'archivio (oltre a quelli dei pezzi aggiunti). */
  eventId?: string;
}

export interface CodaInvio {
  /** Salva nell'archivio, poi invia (in sottofondo): la promessa si risolve a pezzo salvato. */
  aggiungi(p: PezzoInAttesa): Promise<void>;
  /** Rilegge l'archivio dell'evento, riparte se ferma e attende che tutto sia inviato (o un 4xx). */
  svuota(): Promise<void>;
  /** Pezzi e segnaposti non ancora arrivati al server. */
  inAttesa(): number;
  /** true dopo un rifiuto definitivo (4xx) finché `svuota()` non riparte. */
  fermo(): boolean;
  onCambio(cb: (inAttesa: number) => void): () => void;
  onErrore(cb: (messaggio: string, pezzo: PezzoInAttesa) => void): () => void;
  /**
   * Avvisi che non fermano la coda: archivio che non salva (`AVVISO_SOLO_MEMORIA`, alla prima
   * volta e di nuovo se ricapita dopo che tutto è arrivato) o che non toglie (una volta sola).
   */
  onAvviso(cb: (messaggio: string) => void): () => void;
  /** true se qualche pezzo in attesa non è nell'archivio (si perderebbe chiudendo la pagina). */
  soloInMemoria(): boolean;
  /** Toglie dall'archivio tutti i pezzi rimasti di un audio (es. terminato accettando i mancanti). */
  scartaAudio(audioId: string): Promise<void>;
  /** Smette di inviare e di ascoltare `online` (i pezzi restano nell'archivio). */
  chiudi(): void;
}

export function creaCodaInvio({ archivio, invia, attendi = attesaPredefinita, eventId }: OpzioniCodaInvio): CodaInvio {
  const pendenti = new Map<string, PezzoInAttesa>();
  const eventi = new Set<string>(eventId ? [eventId] : []);
  const ascoltatoriCambio = new Set<(n: number) => void>();
  const ascoltatoriErrore = new Set<(m: string, p: PezzoInAttesa) => void>();
  const ascoltatoriAvviso = new Set<(m: string) => void>();
  const nonArchiviati = new Set<string>();
  let avvisatoArchivioNonPulito = false;
  let ciclo: Promise<void> | null = null;
  let isFermo = false;
  let chiuso = false;
  let fallimenti = 0;
  let sveglia: (() => void) | null = null;
  let ultimoConteggio = -1;

  const notificaCambio = () => {
    const n = pendenti.size;
    if (n === ultimoConteggio) return;
    ultimoConteggio = n;
    ascoltatoriCambio.forEach(cb => { try { cb(n); } catch { /* ascoltatore guasto */ } });
  };
  const notificaErrore = (messaggio: string, p: PezzoInAttesa) => {
    ascoltatoriErrore.forEach(cb => { try { cb(messaggio, p); } catch { /* ascoltatore guasto */ } });
  };

  const notificaAvviso = (messaggio: string) => {
    ascoltatoriAvviso.forEach(cb => { try { cb(messaggio); } catch { /* ascoltatore guasto */ } });
  };

  /** Toglie dalla coda (e dall'archivio) un pezzo arrivato o scartato. */
  const togli = async (chiave: string) => {
    if (!nonArchiviati.delete(chiave)) {
      // Se non riesce, il pezzo si rinvierà alla prossima rilettura: il server è idempotente.
      try {
        await archivio.togli(chiave);
      } catch {
        if (!avvisatoArchivioNonPulito) {
          avvisatoArchivioNonPulito = true;
          notificaAvviso(AVVISO_ARCHIVIO_NON_PULITO);
        }
      }
    }
    pendenti.delete(chiave);
    notificaCambio();
  };

  async function esegui(): Promise<void> {
    while (!chiuso && !isFermo) {
      const p = ordinaPezzi(Array.from(pendenti.values()))[0];
      if (!p) return;
      try {
        await invia(p);
      } catch (err) {
        if (chiuso) return;
        if (!pendenti.has(p.chiave)) continue; // scartato nel frattempo
        if (!riprovabile(err)) {
          if (p.tipo === 'segnaposto' && segnapostoDaScartare(err)) {
            // Un momento segnato rifiutato non deve bloccare l'audio: si avvisa e si toglie.
            await togli(p.chiave);
            notificaErrore(messaggioDi(err), p);
            continue;
          }
          isFermo = true;
          notificaErrore(messaggioDi(err), p);
          return;
        }
        const ms = ATTESE_TENTATIVI_MS[Math.min(fallimenti, ATTESE_TENTATIVI_MS.length - 1)];
        fallimenti += 1;
        await new Promise<void>(resolve => {
          sveglia = resolve;
          attendi(ms).then(resolve, resolve);
        });
        sveglia = null;
        continue;
      }
      fallimenti = 0;
      await togli(p.chiave);
    }
  }

  function avvia() {
    if (ciclo || isFermo || chiuso) return;
    ciclo = esegui().finally(() => {
      ciclo = null;
      if (pendenti.size && !isFermo && !chiuso) avvia();
    });
  }

  async function rileggiArchivio() {
    for (const ev of Array.from(eventi)) {
      try {
        for (const p of await archivio.elenca(ev)) if (!pendenti.has(p.chiave)) pendenti.set(p.chiave, p);
      } catch { /* archivio non leggibile: si invia ciò che è in memoria */ }
    }
  }

  const coda: CodaInvio = {
    async aggiungi(p) {
      eventi.add(p.eventId);
      let salvato = true;
      try {
        await archivio.salva(p);
      } catch {
        salvato = false; // archivio pieno o guasto: resta in memoria e si invia comunque
      }
      if (salvato) {
        nonArchiviati.delete(p.chiave);
      } else {
        const primo = nonArchiviati.size === 0;
        nonArchiviati.add(p.chiave);
        if (primo) notificaAvviso(AVVISO_SOLO_MEMORIA);
      }
      if (chiuso) return;
      pendenti.set(p.chiave, p);
      notificaCambio();
      avvia();
    },

    async svuota() {
      if (chiuso) return;
      await rileggiArchivio();
      if (chiuso) return;
      if (isFermo) {
        isFermo = false;
        fallimenti = 0;
      }
      notificaCambio();
      sveglia?.();
      avvia();
      while (ciclo) await ciclo;
    },

    inAttesa: () => pendenti.size,
    fermo: () => isFermo,
    soloInMemoria: () => nonArchiviati.size > 0,

    onCambio(cb) {
      ascoltatoriCambio.add(cb);
      return () => { ascoltatoriCambio.delete(cb); };
    },
    onErrore(cb) {
      ascoltatoriErrore.add(cb);
      return () => { ascoltatoriErrore.delete(cb); };
    },
    onAvviso(cb) {
      ascoltatoriAvviso.add(cb);
      return () => { ascoltatoriAvviso.delete(cb); };
    },

    async scartaAudio(audioId) {
      await rileggiArchivio();
      const daTogliere = Array.from(pendenti.values()).filter(p => p.audioId === audioId);
      for (const p of daTogliere) await togli(p.chiave);
      notificaCambio();
      if (isFermo) {
        isFermo = false;
        fallimenti = 0;
      }
      avvia();
    },

    chiudi() {
      chiuso = true;
      sveglia?.();
      if (typeof window !== 'undefined') window.removeEventListener('online', alRitornoRete);
    },
  };

  function alRitornoRete() {
    fallimenti = 0;
    sveglia?.();
    coda.svuota().catch(() => undefined);
  }
  if (typeof window !== 'undefined') window.addEventListener('online', alRitornoRete);

  return coda;
}

/** `invia` predefinito della coda: pezzo ⇒ PUT binario, segnaposto ⇒ POST /segnaposti. */
export async function inviaAlServer(p: PezzoInAttesa): Promise<void> {
  if (p.tipo === 'pezzo') {
    if (!p.blob || typeof p.indice !== 'number') {
      throw new ErroreDefinitivo('Un pezzo della registrazione non è leggibile dal browser');
    }
    await inviaPezzo(p.eventId, p.audioId, p.ripresa, p.indice, p.blob);
    return;
  }
  if (typeof p.msRipresa !== 'number' || !p.etichetta) throw new ErroreDefinitivo('Momento segnato non valido');
  await aggiungiSegnaposto(p.eventId, { audioId: p.audioId, ripresa: p.ripresa, msRipresa: p.msRipresa, etichetta: p.etichetta });
}
